"use client";
/**
 * The share dialog's own toast (the mock's `.toast`). The app's toast lives outside the
 * modal dialog, where Radix hides it from screen readers, keeps focus away from it, and
 * reads a press on it as a press outside that closes the dialog. This one is rendered
 * inside DialogContent: a polite status line, one message at a time, and its Undo is a
 * real button inside the dialog's focus trap (Tab from Done reaches it).
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export const TOAST_MS = 3200;
export const UNDO_TOAST_MS = 6000;
/** The fade out, before the message leaves the DOM. */
const EXIT_MS = 180;

interface Shown {
  key: number;
  text: string;
  undo?: () => void;
  leaving: boolean;
}

type Timer = ReturnType<typeof setTimeout> | undefined;

/** Whether focus got here by keyboard. jsdom may not know the selector: assume a pointer. */
function byKeyboard(el: Element): boolean {
  try {
    return el.matches(":focus-visible");
  } catch {
    return false;
  }
}

/**
 * `returnFocus` is where keyboard focus goes when a message leaves (timed out, replaced,
 * or its Undo pressed) while focus is inside it, so a keyboard never falls out of the
 * dialog. A tap goes to the dialog itself instead, so a phone keyboard never pops up.
 */
export function useDialogToast(returnFocus: () => void) {
  const [toast, setToast] = useState<Shown | null>(null);
  /** The same message, readable from handlers without a stale render's copy. */
  const current = useRef<Shown | null>(null);
  const pill = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const life = useRef<Timer>(undefined);
  const exit = useRef<Timer>(undefined);
  /** A pointer resting on the toast, or focus inside it, stops its clock. */
  const holds = useRef({ hover: false, focus: false });
  /** The toast whose Undo already ran: a press during the fade still counts, once. */
  const acted = useRef<number | null>(null);
  const refocus = useRef(returnFocus);
  useEffect(() => {
    refocus.current = returnFocus;
  });

  const show = useCallback((t: Shown | null) => {
    current.current = t;
    setToast(t);
  }, []);

  /** Focus inside the message goes back to the dialog before the message goes. */
  const handBack = useCallback(() => {
    const el = document.activeElement;
    const box = pill.current;
    if (!el || !box?.contains(el)) return;
    if (byKeyboard(el)) refocus.current();
    else box.closest<HTMLElement>('[role="dialog"]')?.focus();
  }, []);

  const leave = useCallback(
    (key: number) => {
      clearTimeout(life.current);
      const t = current.current;
      if (!t || t.key !== key) return;
      show({ ...t, leaving: true });
      handBack();
      exit.current = setTimeout(() => {
        if (current.current?.key === key) show(null);
      }, EXIT_MS);
    },
    [show, handBack]
  );

  const arm = useCallback(
    (t: Shown) => {
      clearTimeout(life.current);
      life.current = setTimeout(
        () => leave(t.key),
        t.undo ? UNDO_TOAST_MS : TOAST_MS
      );
    },
    [leave]
  );

  /** Show one message, replacing whatever is showing. A message with Undo takes focus. */
  const say = useCallback(
    (text: string, undo?: () => void) => {
      clearTimeout(exit.current);
      handBack();
      holds.current = { hover: false, focus: false };
      const next = { key: (seq.current += 1), text, undo, leaving: false };
      show(next);
      arm(next);
    },
    [arm, show, handBack]
  );

  const hold = (kind: "hover" | "focus", on: boolean) => {
    holds.current[kind] = on;
    const t = current.current;
    if (on) clearTimeout(life.current);
    else if (t && !t.leaving && !holds.current.hover && !holds.current.focus)
      arm(t);
  };

  const onUndo = () => {
    const t = current.current;
    if (!t?.undo || acted.current === t.key) return;
    acted.current = t.key;
    leave(t.key);
    t.undo();
  };

  useEffect(
    () => () => {
      clearTimeout(life.current);
      clearTimeout(exit.current);
    },
    []
  );

  return { say, toastProps: { toast, pill, hold, onUndo } };
}

/**
 * Hangs just below the dialog, centred, like the mock (where it sits on the backdrop under
 * the dialog), so it never covers Done or Copy link. On a phone the dialog is a bottom
 * sheet, so it floats just above the sheet instead. The status element is always mounted,
 * so a screen reader hears each message; each message is its own node (keyed), so the same
 * words twice are heard twice.
 */
export function DialogToast({
  toast,
  pill,
  hold,
  onUndo,
}: ReturnType<typeof useDialogToast>["toastProps"]) {
  const undoButton = useRef<HTMLButtonElement>(null);
  const messageId = useId();
  const focusKey = toast?.undo && !toast.leaving ? toast.key : null;
  // A message with Undo takes focus when it arrives (after a removal), so the keyboard
  // is one Enter away from putting the person back. Only focus that was dropped (the
  // removed row's button left with the row): the answer is a round trip late, and by
  // then the person may be typing in the field or holding a menu open.
  useEffect(() => {
    const undo = undoButton.current;
    if (focusKey === null || !undo) return;
    const at = document.activeElement;
    if (!at || at === document.body || at === undo.closest('[role="dialog"]'))
      undo.focus();
  }, [focusKey]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none absolute inset-x-0 z-10 flex justify-center px-4 max-sm:bottom-full max-sm:mb-3 sm:top-full sm:mt-3"
    >
      {toast && (
        <div
          key={toast.key}
          ref={pill}
          onMouseEnter={() => hold("hover", true)}
          onMouseLeave={() => hold("hover", false)}
          onFocus={() => hold("focus", true)}
          onBlur={() => hold("focus", false)}
          className={cn(
            "pointer-events-auto flex max-w-full items-center gap-3.5 rounded-12 bg-fg-default px-3.5 py-2.5 text-3.5 text-bg-base shadow-lg",
            "transition-[opacity,translate] duration-[180ms] motion-reduce:transition-none",
            toast.leaving
              ? "translate-y-3 opacity-0"
              : "animate-in fade-in-0 slide-in-from-bottom-3 duration-[180ms] motion-reduce:animate-none"
          )}
        >
          <span id={messageId}>{toast.text}</span>
          {toast.undo && (
            <button
              ref={undoButton}
              type="button"
              aria-describedby={messageId}
              onClick={onUndo}
              className="shrink-0 font-semibold text-bg-base underline underline-offset-3"
            >
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
